import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import DiscretionaryAllocationsEditor from "./DiscretionaryAllocationsEditor";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import Panel from "../../components/brand/Panel";
import {
  getApportionmentChecks,
  getBudget,
  getBudgets,
  updateSectionAllocations,
} from "../../api/financeApi";
import { getUnitGroups, getUnitGroupUnits } from "../../api/unitApi";
import { getApiErrorMessage } from "../../utils/concurrency";

async function fetchAllGroupUnitIds(propertyId, groupId) {
  const ids = [];
  let page = 1;
  const pageSize = 100;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const data = await getUnitGroupUnits(propertyId, groupId, { page, page_size: pageSize });
    const results = data?.results || [];
    results.forEach((unit) => ids.push(unit.id));
    const total = data?.total ?? results.length;
    if (page * pageSize >= total || results.length === 0) break;
    page += 1;
  }
  return ids;
}

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

const MECHANISM_OPTIONS = [
  { value: "equal_shares", label: "Equal shares" },
  { value: "floor_area", label: "Floor area" },
  { value: "discretionary", label: "Discretionary" },
];

const mechanismLabel = (value) =>
  MECHANISM_OPTIONS.find((option) => option.value === value)?.label || "—";

function unitLabel(unit) {
  return unit.unit_name || unit.unit_code || `Unit ${unit.id ?? unit.unit_id}`;
}

function unitId(unit) {
  return unit.id ?? unit.unit_id;
}

export default function ApportionmentsPanel({
  propertyId,
  propertyUnits = [],
  budgetIdParam,
  onBudgetChange,
  onMessage,
  onError,
}) {
  const [budgets, setBudgets] = useState([]);
  const [budget, setBudget] = useState(null);
  const [sectionId, setSectionId] = useState("");
  const [mechanism, setMechanism] = useState("equal_shares");
  const [search, setSearch] = useState("");
  const [availableSelected, setAvailableSelected] = useState([]);
  const [assignedSelected, setAssignedSelected] = useState([]);
  const [lastAvailableIndex, setLastAvailableIndex] = useState(null);
  const [lastAssignedIndex, setLastAssignedIndex] = useState(null);
  const [assignedIds, setAssignedIds] = useState(new Set());
  const [unitGroups, setUnitGroups] = useState([]);
  const [groupToAdd, setGroupToAdd] = useState("");
  const [addingGroup, setAddingGroup] = useState(false);
  const [checks, setChecks] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const sections = budget?.sections || [];
  const estateSection = sections.find((s) => s.section_type === "estate");
  const blockSections = sections.filter((s) => s.section_type === "block");
  const section = sections.find((s) => String(s.id) === String(sectionId));
  const readOnly = budget?.status === "published";

  const loadBudgets = useCallback(async () => {
    const res = await getBudgets(propertyId, 1, { page_size: 100 });
    setBudgets(res.data.results || []);
  }, [propertyId]);

  const loadBudget = useCallback(
    async (id) => {
      if (!id) {
        setBudget(null);
        return;
      }
      const res = await getBudget(propertyId, id);
      setBudget(res.data);
      setSectionId("");
    },
    [propertyId]
  );

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        onError?.("");
        await loadBudgets();
        if (budgetIdParam) {
          await loadBudget(budgetIdParam);
        }
      } catch (e) {
        onError?.(getApiErrorMessage(e, "Failed to load apportionments."));
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [budgetIdParam, loadBudget, loadBudgets, onError]);

  useEffect(() => {
    let cancelled = false;
    const loadGroups = async () => {
      try {
        const data = await getUnitGroups(propertyId);
        if (!cancelled) {
          setUnitGroups(Array.isArray(data) ? data : data.results || []);
        }
      } catch (e) {
        if (!cancelled) setUnitGroups([]);
      }
    };
    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  useEffect(() => {
    if (!section || section.section_type !== "block") {
      setAssignedIds(new Set());
      return;
    }
    const ids = new Set((section.unit_allocations || []).map((row) => row.unit_id));
    setAssignedIds(ids);
    setMechanism(section.mechanism || "equal_shares");
    setAvailableSelected([]);
    setAssignedSelected([]);
    setLastAvailableIndex(null);
    setLastAssignedIndex(null);
    setGroupToAdd("");
  }, [section]);

  const availableUnits = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (propertyUnits || []).filter((unit) => {
      if (assignedIds.has(unitId(unit))) return false;
      if (!term) return true;
      return unitLabel(unit).toLowerCase().includes(term);
    });
  }, [propertyUnits, assignedIds, search]);

  const assignedUnits = useMemo(() => {
    return (propertyUnits || [])
      .filter((unit) => assignedIds.has(unitId(unit)))
      .map((unit) => {
        const id = unitId(unit);
        const saved = (section?.unit_allocations || []).find((row) => row.unit_id === id);
        return {
          unit_id: id,
          unit_name: unitLabel(unit),
          percentage: saved?.percentage,
        };
      });
  }, [propertyUnits, assignedIds, section]);

  const totalPercentage = assignedUnits.reduce(
    (sum, unit) => sum + (Number(unit.percentage) || 0),
    0
  );

  const sectionTotal = (item) =>
    (item.lines || []).reduce((sum, line) => sum + (Number(line.amount) || 0), 0);

  const handleAvailableClick = (index, event) => {
    const id = unitId(availableUnits[index]);
    if (event.shiftKey && lastAvailableIndex != null) {
      const start = Math.min(lastAvailableIndex, index);
      const end = Math.max(lastAvailableIndex, index);
      const rangeIds = availableUnits.slice(start, end + 1).map(unitId);
      setAvailableSelected((prev) => Array.from(new Set([...prev, ...rangeIds])));
    } else {
      setAvailableSelected((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
      );
    }
    setLastAvailableIndex(index);
  };

  const handleAssignedClick = (index, event) => {
    const id = assignedUnits[index].unit_id;
    if (event.shiftKey && lastAssignedIndex != null) {
      const start = Math.min(lastAssignedIndex, index);
      const end = Math.max(lastAssignedIndex, index);
      const rangeIds = assignedUnits.slice(start, end + 1).map((u) => u.unit_id);
      setAssignedSelected((prev) => Array.from(new Set([...prev, ...rangeIds])));
    } else {
      setAssignedSelected((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
      );
    }
    setLastAssignedIndex(index);
  };

  const assignUnits = () => {
    if (!availableSelected.length) return;
    setAssignedIds((prev) => new Set([...prev, ...availableSelected]));
    setAvailableSelected([]);
  };

  const unassignUnits = () => {
    if (!assignedSelected.length) return;
    setAssignedIds((prev) => {
      const next = new Set(prev);
      assignedSelected.forEach((id) => next.delete(id));
      return next;
    });
    setAssignedSelected([]);
  };

  const addGroupUnits = async () => {
    if (!groupToAdd) return;
    try {
      setAddingGroup(true);
      onError?.("");
      const groupUnitIds = await fetchAllGroupUnitIds(propertyId, groupToAdd);
      const validIds = new Set((propertyUnits || []).map((unit) => unitId(unit)));
      setAssignedIds((prev) => {
        const next = new Set(prev);
        groupUnitIds.forEach((id) => {
          if (validIds.has(id)) next.add(id);
        });
        return next;
      });
      setGroupToAdd("");
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to add units from group."));
    } finally {
      setAddingGroup(false);
    }
  };

  const handleSave = async () => {
    if (!budget || !section || section.section_type !== "block") return;
    try {
      setSaving(true);
      onError?.("");
      await updateSectionAllocations(propertyId, budget.id, section.id, {
        row_version: section.row_version,
        unit_ids: [...assignedIds],
        mechanism,
      });
      await loadBudget(budget.id);
      setSectionId(String(section.id));
      onMessage?.(`Units saved for ${section.name}.`);
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to save unit assignments."));
    } finally {
      setSaving(false);
    }
  };

  const allocationSaved = (savedSectionId, data) => {
    setBudget((current) => ({
      ...current,
      sections: current.sections.map((item) => {
        if (item.id !== savedSectionId) return item;
        const allocations = new Map((item.unit_allocations || []).map((row) => [row.unit_id, row]));
        data.results.forEach((row) => allocations.set(row.unit_id, row));
        return { ...item, row_version: data.row_version, unit_allocations: [...allocations.values()] };
      }),
    }));
  };

  const runChecks = async () => {
    if (!budget) return;
    try {
      const res = await getApportionmentChecks(propertyId, budget.id);
      setChecks(res.data.checks || []);
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to check apportionments."));
    }
  };

  const estateTotal = estateSection ? sectionTotal(estateSection) : 0;
  const estateUnitCount =
    (propertyUnits || []).length || (estateSection?.unit_allocations || []).length;

  return (
    <Panel title="Apportionments" noBodyPadding={false}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The Estate Charge applies to every unit in the property automatically. Assign units to each
        Block Charge. For Discretionary, save the assignments, then edit amounts and reasons below.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Budget</InputLabel>
          <Select
            label="Budget"
            value={budget ? String(budget.id) : ""}
            onChange={(e) => {
              const id = e.target.value;
              onBudgetChange?.(id);
              loadBudget(id);
            }}
          >
            {budgets.map((b) => (
              <MenuItem key={b.id} value={String(b.id)}>
                {b.name} ({b.status})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" disabled={!budget} onClick={runChecks}>
          Check totals
        </Button>
      </Box>

      {checks && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {checks.map((row) => (
            <div key={row.section_id}>
              {row.section_name}: {row.total_percentage?.toFixed?.(2) ?? row.total_percentage}%{" "}
              {row.within_tolerance ? "✓" : "⚠"}
            </div>
          ))}
        </Alert>
      )}

      {!budget ? (
        <Typography color="text.secondary">Select a budget to view apportionments.</Typography>
      ) : (
        <>
          {estateSection && (
            <Box
              sx={{
                p: 2,
                mb: 3,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                alignItems: "baseline",
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Estate Charge
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {money(estateTotal)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Split across {estateUnitCount} unit{estateUnitCount === 1 ? "" : "s"}
              </Typography>
            </Box>
          )}

          {estateSection?.effective_mechanism === "discretionary" && (
            <DiscretionaryAllocationsEditor key={`estate-${budget.id}-${estateSection.id}`} propertyId={propertyId} budgetId={budget.id} sectionId={estateSection.id} revision={estateSection.row_version} onMessage={onMessage} onSaved={(data) => allocationSaved(estateSection.id, data)} />
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Block Charges
          </Typography>
          {blockSections.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This budget has no Block Charges.
            </Typography>
          ) : (
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Block Charge</TableCell>
                    <TableCell>Mechanism</TableCell>
                    <TableCell align="right">Units</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Allocated %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blockSections.map((block) => {
                    const allocations = block.unit_allocations || [];
                    const allocated = allocations.reduce(
                      (sum, row) => sum + (Number(row.percentage) || 0),
                      0
                    );
                    const selected = String(block.id) === String(sectionId);
                    return (
                      <TableRow
                        key={block.id}
                        hover
                        selected={selected}
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          setSectionId(selected ? "" : String(block.id))
                        }
                      >
                        <TableCell>{block.name}</TableCell>
                        <TableCell>{mechanismLabel(block.mechanism)}</TableCell>
                        <TableCell align="right">{allocations.length}</TableCell>
                        <TableCell align="right">{money(sectionTotal(block))}</TableCell>
                        <TableCell align="right">{allocated.toFixed(2)}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {section && section.section_type === "block" && (
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {section.name}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ minWidth: 180 }} disabled={readOnly}>
                    <InputLabel>Mechanism</InputLabel>
                    <Select
                      label="Mechanism"
                      value={mechanism}
                      onChange={(e) => setMechanism(e.target.value)}
                    >
                      {MECHANISM_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!readOnly && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={saving}
                      onClick={handleSave}
                      startIcon={<SaveIcon />}
                    >
                      Save
                    </Button>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { md: "1fr auto 1fr" }, gap: 2 }}>
                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Available units
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search units…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  {!readOnly && unitGroups.length > 0 && (
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                      <FormControl size="small" sx={{ flexGrow: 1 }}>
                        <InputLabel id="apportionment-group-add-label">
                          Add from unit group
                        </InputLabel>
                        <Select
                          labelId="apportionment-group-add-label"
                          label="Add from unit group"
                          value={groupToAdd}
                          onChange={(e) => setGroupToAdd(e.target.value)}
                        >
                          {unitGroups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                              {group.name}
                              {group.unit_count != null
                                ? ` (${group.unit_count} unit${group.unit_count === 1 ? "" : "s"})`
                                : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!groupToAdd || addingGroup}
                        onClick={addGroupUnits}
                        startIcon={<AddIcon />}
                      >
                        Add
                      </Button>
                    </Box>
                  )}
                  <List dense sx={{ maxHeight: 360, overflow: "auto" }}>
                    {availableUnits.map((unit, index) => {
                      const id = unitId(unit);
                      const checked = availableSelected.includes(id);
                      return (
                        <ListItemButton
                          key={id}
                          selected={checked}
                          onClick={(event) => handleAvailableClick(index, event)}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                          </ListItemIcon>
                          <ListItemText primary={unitLabel(unit)} />
                        </ListItemButton>
                      );
                    })}
                    {availableUnits.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                        No units available.
                      </Typography>
                    )}
                  </List>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <IconButton
                    color="primary"
                    disabled={readOnly || availableSelected.length === 0}
                    onClick={assignUnits}
                    aria-label="Assign units"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                  <IconButton
                    color="primary"
                    disabled={readOnly || assignedSelected.length === 0}
                    onClick={unassignUnits}
                    aria-label="Remove units"
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </Box>

                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle2">Units in {section.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total {totalPercentage.toFixed(2)}%
                    </Typography>
                  </Box>
                  <List dense sx={{ maxHeight: 360, overflow: "auto" }}>
                    {assignedUnits.map((unit, index) => {
                      const checked = assignedSelected.includes(unit.unit_id);
                      const amount =
                        unit.percentage != null
                          ? (sectionTotal(section) * Number(unit.percentage)) / 100
                          : null;
                      return (
                        <ListItemButton
                          key={unit.unit_id}
                          selected={checked}
                          onClick={(event) => handleAssignedClick(index, event)}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                          </ListItemIcon>
                          <ListItemText
                            primary={unit.unit_name}
                            secondary={
                              unit.percentage != null
                                ? `${Number(unit.percentage).toFixed(2)}% · ${money(amount)}`
                                : "Calculated on save"
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                    {assignedUnits.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                        No units assigned yet.
                      </Typography>
                    )}
                  </List>
                </Box>
              </Box>
              {section.mechanism === "discretionary" && (
                <DiscretionaryAllocationsEditor key={section.id} propertyId={propertyId} budgetId={budget.id} sectionId={section.id} revision={section.row_version} onMessage={onMessage} onSaved={(data) => allocationSaved(section.id, data)} />
              )}
            </Box>
          )}
        </>
      )}

      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading…
        </Typography>
      )}
    </Panel>
  );
}
